import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  AdAvatar,
  Empty,
  Section,
  ToastProvider,
  errText,
  useToast,
} from "../admin/ui";
import { startImpersonation } from "../services/impersonation";
import {
  api,
  type AdminCompanyRow,
  type ImpersonationTarget,
  type Me,
} from "../services/api";

function roleLabel(role: string): string {
  switch (role) {
    case "client_admin":
      return "Client admin";
    case "approver":
      return "Approver";
    case "end_user":
      return "User";
    default:
      return role;
  }
}

/**
 * View as user.
 *
 * Two columns: pick the customer, then the person. The session is strictly
 * read-only (the backend blocks every write while impersonating) and is exited
 * from the banner that appears on every page.
 */
export function AdminViewAs() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  if (!me) return <div className="card">Loading…</div>;

  const isStaff = me.role === "iquest_admin" || me.role === "service_desk";
  if (!isStaff) {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">
          Previewing the portal as another user is reserved for iQuest staff.
        </p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <AdminViewAsInner />
    </ToastProvider>
  );
}

function AdminViewAsInner() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const companyId = params.get("c") ?? "";

  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: companies } = useSWR<AdminCompanyRow[]>(
    "/api/admin/forms/companies",
    () => api.admin.forms.companies(),
  );

  const { data: targets, isLoading } = useSWR(
    companyId ? ["/api/admin/impersonation/targets", companyId, search] : null,
    () => api.impersonation.targets(companyId, search.trim() || undefined),
    { keepPreviousData: true },
  );

  const visibleCompanies = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = companies ?? [];
    return q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
  }, [companies, filter]);

  const selected = (companies ?? []).find((c) => c.id === companyId);

  async function viewAs(target: ImpersonationTarget) {
    if (
      !window.confirm(
        `View the portal as ${target.display_name}? It is a read-only preview; exit it any time from the banner at the top.`,
      )
    ) {
      return;
    }
    setError(null);
    setStartingId(target.id);
    toast(`Opening the portal as ${target.display_name} (read-only)`);
    try {
      // Navigates away on success (hard reload as the target user).
      await startImpersonation(target.id);
    } catch (e) {
      setError(errText(e, "Could not start the preview."));
      setStartingId(null);
    }
  }

  return (
    <div className="rf-wrap">
      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">Admin</div>
          <h1 className="rf-h1">View portal as a user</h1>
          <p className="rf-sub">
            See exactly what someone sees — their tickets, forms and approvals. Read-only,
            and every session is audit-logged.
          </p>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="ad-viewas">
        <Section title="1. Customer">
          <div className="rf-search" style={{ marginBottom: 12, maxWidth: "none" }}>
            <Icon name="search" />
            <input
              type="search"
              value={filter}
              placeholder="Filter customers"
              aria-label="Filter customers"
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          {visibleCompanies.length === 0 ? (
            <Empty title="No customers match" blurb="Try a different filter." />
          ) : (
            visibleCompanies.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`ad-pick${c.id === companyId ? " sel" : ""}`}
                onClick={() => {
                  setSearch("");
                  setParams({ c: c.id }, { replace: true });
                }}
              >
                <AdAvatar name={c.name} />
                <span className="bd">
                  <span className="t">{c.name}</span>
                </span>
              </button>
            ))
          )}
        </Section>

        <Section title="2. Person" desc={selected ? `at ${selected.name}` : undefined}>
          {!companyId ? (
            <div className="ad-note">Pick a customer first.</div>
          ) : (
            <>
              <div className="rf-search" style={{ marginBottom: 12, maxWidth: "none" }}>
                <Icon name="search" />
                <input
                  type="search"
                  value={search}
                  placeholder="Search name or email"
                  aria-label="Search people"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {isLoading && !targets ? (
                <div className="ad-note">Loading people…</div>
              ) : (targets ?? []).length === 0 ? (
                <Empty
                  title="No users yet"
                  blurb="Add people from the customer's page first."
                  action={
                    <Link
                      className="btn btn-ghost btn-sm"
                      to={`/admin/clients/${companyId}/users`}
                    >
                      Users &amp; access
                    </Link>
                  }
                />
              ) : (
                (targets ?? []).map((u) => (
                  <div key={u.id} className="ad-pick static">
                    <AdAvatar name={u.display_name} />
                    <span className="bd">
                      <span className="t">{u.display_name}</span>
                      <span className="s">
                        {roleLabel(u.role)} · {u.email}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={startingId !== null}
                      onClick={() => void viewAs(u)}
                    >
                      <Icon name="eye" size={14} />{" "}
                      {startingId === u.id ? "Starting…" : "View as"}
                    </button>
                  </div>
                ))
              )}
            </>
          )}
        </Section>
      </div>
    </div>
  );
}
